import React from 'react';
import styled from 'styled-components';

import Button from '../../../Button';
import Dropdown, {
  StyledButton,
  StyledContent,
  StyledDropdownIcon,
} from '../../../Dropdown';
import Input from '../../../Input';
import OnClickOutside from '../../../OnClickOutside';
import RoundButton, { Types as ButtonTypes } from '../RoundButton';
import Throbber from '../../../Throbber';

import { IncomingCall, DownGreen } from '@icons/dialer';
import { Alpha } from '@icons/dialer/partners';

const DEFAULT_PLACEHOLDER = 'Введите номер телефона';

const NOT_NUMBERIC_REGEX = /\D+/g;
const STARTS_WITH_8_REGEX = /^8/;

type CallType = {
  name?: string;
  number: string;
};

interface RecentCallsProps {
  className?: string;
  calls: CallType[] | null;
  value: string;
  isAlfaTravelManager: boolean;
  onCall: ({ value, partner }: { value: string; partner?: string }) => void;
  onToggle: ({ isOpen }: { isOpen: boolean }) => void;
}

interface RecentCallsState {
  isOpen: boolean;
  value: RecentCallsProps['value'];
}

class RecentCalls extends React.PureComponent<
  RecentCallsProps,
  RecentCallsState
> {
  static defaultProps = {
    calls: null,
    className: '',
    value: '',
    isAlfaTravelManager: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
      isOpen: false,
    };

    this.handleClick = this.handleClick.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.inputRenderer = this.inputRenderer.bind(this);
    this.listRenderer = this.listRenderer.bind(this);
    this.partnersHeadRenderer = this.partnersHeadRenderer.bind(this);
    this.partnersListRenderer = this.partnersListRenderer.bind(this);
  }

  handleClick(value) {
    this.setState({ value }, this.handleOutsideClick);
  }

  handleInput({ currentTarget }) {
    const { value: unformattedValue } = currentTarget;
    let value = unformattedValue.replace(NOT_NUMBERIC_REGEX, '');

    const startsWith8 = STARTS_WITH_8_REGEX.test(value);
    if (value.length === 11 && startsWith8) {
      value = value.replace(/^8/, '7');
    }

    this.setState({ value });
  }

  handleKeyPress({ key }) {
    if (key === 'Escape') {
      this.setState({ value: '' });
    }

    if (key === 'Enter') {
      this.handleSubmit();
    }
  }

  handleOutsideClick() {
    const { isOpen } = this.state;
    if (isOpen) {
      this.handleToggle();
    }
  }

  handleToggle() {
    const { onToggle } = this.props;

    this.setState(
      prevState => ({
        isOpen: !prevState.isOpen,
      }),
      () => {
        const { isOpen } = this.state;
        onToggle({ isOpen });
      }
    );
  }

  handleSubmit({
    currentTarget,
  }: { currentTarget?: HTMLElement & { dataset?: DOMStringMap } } = {}) {
    const { onCall } = this.props;
    const { value } = this.state;

    const partner = currentTarget?.dataset?.partner as string | undefined;

    onCall({ value, partner });
  }

  listRenderer() {
    const { calls } = this.props;

    if (!calls) {
      return <StyledThrobber size={16} color="grey" />;
    }

    if (calls.length === 0) {
      return <StyledEmpty>Нет последних вызовов</StyledEmpty>;
    }

    return (
      <StyledCallList>
        {calls.map(({ name, number }) => (
          <li>
            <StyledCall functional onClick={() => this.handleClick(number)}>
              <StyledCallIcon />
              <StyledCallInfo>
                <StyledNumber>{number}</StyledNumber>
                {name && <StyledName>{name}</StyledName>}
              </StyledCallInfo>
            </StyledCall>
          </li>
        ))}
      </StyledCallList>
    );
  }

  inputRenderer() {
    const { value } = this.state;
    return (
      <StyledInput
        value={value}
        onChange={this.handleInput}
        onKeyDown={this.handleKeyPress}
        placeholder={DEFAULT_PLACEHOLDER}
      />
    );
  }

  partnersHeadRenderer() {
    return <StyledDownGreen width={16} height={16} />;
  }

  partnersListRenderer() {
    return (
      <StyledAlphaButton onClick={this.handleSubmit} data-partner="alpha">
        <Alpha width={98} height={16} />
      </StyledAlphaButton>
    );
  }

  render() {
    const { value, isOpen } = this.state;
    const { onCall, onToggle, isAlfaTravelManager, ...props } = this.props;

    return (
      <OnClickOutside {...props} onClickOutside={this.handleOutsideClick}>
        <StyledContainer>
          <StyledDropdown
            headRenderer={this.inputRenderer()}
            contentRenderer={this.listRenderer()}
            onToggle={this.handleToggle}
            open={isOpen}
          />
          <StyledCallActionWrapper disabled={value.length < 3}>
            <StyledCallActionIcon
              onClick={this.handleSubmit}
              actionType={ButtonTypes.START_CALL}
            />
            {isAlfaTravelManager && (
              <StyledPartnersDropdown
                headRenderer={this.partnersHeadRenderer()}
                contentRenderer={this.partnersListRenderer()}
                onToggle={() => {}}
              />
            )}
          </StyledCallActionWrapper>
        </StyledContainer>
      </OnClickOutside>
    );
  }
}

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;
  border: none;
  background: none;

  ${StyledButton} {
    border-radius: 4px;
    border: 1px solid #919499;
    width: 200px;
  }

  ${StyledContent} {
    width: 100%;
    padding: 0;
  }

  ${StyledDropdownIcon} {
    position: absolute;
    top: 50%;
    margin-top: -2px;
    right: 10px;
  }
`;

const StyledCallActionWrapper = styled.div<{ disabled: boolean }>`
  position: relative;
  width: 40px;
  height: 40px;
  margin-left: 10px;

  ${({ disabled }) =>
    disabled &&
    `
      opacity: .5;
      pointer-events: none;
  `}
`;

const StyledThrobber = styled(Throbber)`
  padding: 35px 0;
  position: relative;
`;

const StyledEmpty = styled.div`
  width: 100%;
  color: black;
  font-size: 14px;
  line-height: 70px;
  text-align: center;
`;

const StyledInput = styled(Input)`
  width: 100%;
  height: 38px;
  border: none;
  color: white;
  font-size: 13px;
  line-height: 38px;
  border-radius: 4px;
  font-weight: normal;
  padding: 0 27px 0 11px;
  background-color: #16191b;
`;

const StyledDownGreen = styled(DownGreen)`
  position: absolute;
  right: 0;
  bottom: 0;
`;

const StyledAlphaButton = styled(Button)`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 14px;
  height: 32px;
  line-height: 32px;
  padding-top: 2px;
`;

const StyledCallList = styled.ul`
  border-radius: 4px;
  width: 100%;
  padding: 0;

  li {
    border-top: 2px solid #c2c5cc33;
    &:first-child {
      border-width: 0;
    }
  }
`;

const StyledCall = styled(Button)`
  display: flex;
  padding: 0 10px;
  align-items: center;
  height: 43px;
  text-align: left;
  width: 100%;
  line-height: normal;
`;

const StyledCallIcon = styled(IncomingCall)`
  width: 21px;
  margin-right: 5px;
`;

const StyledCallInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledNumber = styled.span`
  color: #17181a;
  font-size: 14px;
`;

const StyledName = styled.span`
  color: #919499;
  font-size: 12px;
`;

const StyledCallActionIcon = styled(RoundButton)`
  border: none;
`;

const StyledPartnersDropdown = styled(Dropdown)`
  position: absolute;
  bottom: -3px;
  right: -3px;
  border: none;
  background: none;

  ${StyledContent} {
    padding: 0;
    margin: 3px -7px 0;

    &:before {
      display: none;
    }
  }

  ${StyledDropdownIcon} {
    display: none;
  }
`;

export default RecentCalls;
